import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import supabase from "../supabase";

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  // Fast-path initial value for instant rendering when arriving via Login.js's
  // navigate(..., { state }) — always re-verified against Supabase below, so
  // a direct visit, refresh, or stale/missing state can't grant access.
  const [profile, setProfile] = useState(location.state?.profile || null);

  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    localStorage.removeItem("superAdminOrgOverride");

    const verifyAndLoad = async () => {
      const { data: userData, error: authError } = await supabase.auth.getUser();
      if (authError || !userData?.user) {
        navigate("/login", { state: { from: location.pathname } });
        return;
      }

      const { data: profileData, error: profileError } = await supabase
        .from("Users")
        .select("*")
        .eq("id", userData.user.id)
        .single();

      // ✅ IMPROVED SECURITY KICK:
      // Accept either the exact 'superAdmin' role OR the 'isTrueSuperAdmin' flag
      const isSuper =
        !profileError &&
        (profileData?.role === "superAdmin" ||
          profileData?.isTrueSuperAdmin === true);

      if (!isSuper) {
        navigate("/login", { state: { from: location.pathname } });
        return;
      }

      setProfile(profileData);

      const { data, error } = await supabase
        .from("Organizations")
        .select("*")
        .order("name", { ascending: true });

      if (error) {
        console.error("Error fetching organizations:", error);
      } else {
        setOrganizations(data || []);
      }
      setLoading(false);
    };

    verifyAndLoad();
  }, [navigate, location]);

  const handleEnterPortal = (org) => {
    localStorage.setItem("superAdminOrgOverride", org.id);
    // 🚨 THE MAGIC OVERRIDE 🚨
    // We clone the superAdmin's profile, but inject the clicked organization's ID
    // and temporarily set their role to "Admin". This allows them to bypass
    // having to rewrite every single "isAdmin" check in your app's frontend.
    const mockedAdminProfile = {
      ...profile,
      company_id: org.id,
      role: "Admin",
      isTrueSuperAdmin: true, // A flag just in case you need to check true identity later
    };

    navigate(`/organization-portal/${org.id}`, {
      state: { profile: mockedAdminProfile },
    });
  };

  const filteredOrgs = organizations.filter((org) =>
    org.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>Loading organizations...</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>Super Admin Dashboard</h1>
          <p style={styles.subtitle}>
            Select an organization to enter their portal.
          </p>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.searchRow}>
          <input
            style={styles.searchInput}
            placeholder="Search organizations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Organization Name</th>
              <th style={styles.th}>Created At</th>
              <th style={{ ...styles.th, textAlign: "right" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrgs.map((org) => (
              <tr key={org.id} style={styles.tr}>
                <td style={styles.tdStrong}>{org.name}</td>
                <td style={styles.td}>
                  {new Date(org.created_at).toLocaleDateString()}
                </td>
                <td style={{ ...styles.td, textAlign: "right" }}>
                  <button
                    style={styles.primaryBtn}
                    onClick={() => handleEnterPortal(org)}
                  >
                    Enter Portal →
                  </button>
                </td>
              </tr>
            ))}
            {filteredOrgs.length === 0 && (
              <tr>
                <td colSpan={3} style={styles.emptyState}>
                  No organizations found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: "40px",
    background: "#f6f7fb",
    minHeight: "100vh",
    color: "#111827",
  },
  headerRow: { marginBottom: "24px" },
  title: { fontSize: 28, fontWeight: 900, margin: 0 },
  subtitle: { fontSize: 14, color: "#6b7280", marginTop: 4 },
  card: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  },
  searchRow: {
    padding: "16px",
    borderBottom: "1px solid #e5e7eb",
    background: "#fafafa",
  },
  searchInput: {
    width: "100%",
    maxWidth: "400px",
    padding: "10px 14px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    fontSize: 14,
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    fontSize: 12,
    fontWeight: 800,
    color: "#6b7280",
    padding: "14px 16px",
    borderBottom: "1px solid #e5e7eb",
    background: "#fff",
    textTransform: "uppercase",
  },
  tr: { borderBottom: "1px solid #f3f4f6" },
  td: { padding: "16px", fontSize: 14, verticalAlign: "middle" },
  tdStrong: {
    padding: "16px",
    fontSize: 14,
    fontWeight: 800,
    verticalAlign: "middle",
  },
  primaryBtn: {
    background: "#00876e",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "8px 14px",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 13,
  },
  emptyState: { padding: 24, textAlign: "center", color: "#6b7280" },
};
